/**
 * Directory Stores Hook
 * 
 * Provides access to directory store data through the StoreSingleton
 * with intelligent caching and search functionality.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { directoryService, DirectoryStore, DirectorySearchResult } from '@/services/DirectorySingletonService';
import { clientLogger } from '@/lib/client-logger';

export interface DirectoryStoresResponse extends DirectorySearchResult {}

export interface UseDirectoryStoresOptions {
  search?: string;
  category?: string;
  city?: string;
  state?: string;
  minRating?: string;
  openNow?: boolean;
  lat?: number;
  lng?: number;
  sort?: string;
  page?: number;
  limit?: number;
  cacheTTL?: number;
}

export interface UseDirectoryStoresReturn {
  stores: DirectoryStore[];
  loading: boolean;
  error: string | null;
  pagination: DirectoryStoresResponse['pagination'] | null;
  /** Open-now listings across the whole filtered set (API-computed). */
  openNowCount: number;
  refetch: () => Promise<void>;
  fromCache: boolean;
  metrics: {
    cacheHits: number;
    cacheMisses: number;
    totalRequests: number;
    averageResponseTime: number;
  };
}

export const useDirectoryStores = (
  options: UseDirectoryStoresOptions = {}
): UseDirectoryStoresReturn => {
  const {
    search,
    category,
    city,
    state,
    minRating,
    openNow,
    lat,
    lng,
    sort = 'activity',
    page = 1,
    limit = 24,
    cacheTTL = 15 * 60 * 1000, // 15 minutes
  } = options;

  const [stores, setStores] = useState<DirectoryStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<DirectoryStoresResponse['pagination'] | null>(null);
  const [openNowCount, setOpenNowCount] = useState(0);
  const [fromCache, setFromCache] = useState(false);
  const [localMetrics, setLocalMetrics] = useState({
    cacheHits: 0,
    cacheMisses: 0,
    totalRequests: 0,
    averageResponseTime: 0,
  });

  const fetchDirectoryStores = useCallback(async () => {
    setLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      // Log the request parameters (no manual cache key needed since service handles caching)
   /*    console.log('[useDirectoryStores] Fetching with params:', {
        search,
        category,
        lat,
        lng,
        sort,
        page,
        limit
      }); */
      
      // Use the directory API endpoint through singleton service
      // Service handles caching automatically through makeDefaultRequest
      const result = await directoryService.searchDirectoryStores({
        search,
        category,
        city,
        state,
        minRating,
        openNow,
        lat,
        lng,
        sort,
        page,
        limit
      });

      if (!result) {
        throw new Error('Failed to fetch directory stores: No response from service');
      }

      // Deduplicate listings by tenant_id (MV returns one row per category)
      // Keep the first occurrence of each store. The API already reports
      // distinct-tenant totals, so pagination stays authoritative.
      if (result.listings) {
        const seenIds = new Set();
        result.listings = result.listings.filter((listing: any) => {
          const id = listing.tenantId || listing.tenant_id || listing.id;
          if (seenIds.has(id)) {
            return false;
          }
          seenIds.add(id);
          return true;
        });
      }

      // Store the fetched stores (service handles caching automatically)
      setStores(result.listings || []);
      setPagination(result.pagination);
      setOpenNowCount(
        result.openNowCount ??
          (result.listings || []).filter((s) => s.isOpen === true).length,
      );
      setFromCache(false);

      const endTime = Date.now();
      const responseTime = endTime - startTime;

      // Get actual service metrics instead of manual tracking
      const serviceMetrics = directoryService.getMetrics();
   //   console.log('[useDirectoryStores] Service metrics:', serviceMetrics);
      
      // Update local metrics to match service metrics
      setLocalMetrics({
        cacheHits: serviceMetrics.cacheHits,
        cacheMisses: serviceMetrics.cacheMisses,
        totalRequests: serviceMetrics.cacheHits + serviceMetrics.cacheMisses,
        averageResponseTime: responseTime
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch directory stores');
      clientLogger.error('[useDirectoryStores] Error:', { detail: err });
    } finally {
      setLoading(false);
    }
  }, [search, category, city, state, minRating, openNow, lat, lng, sort, page, limit, cacheTTL]);

  const refetch = useCallback(() => {
    return fetchDirectoryStores();
  }, [fetchDirectoryStores]);

  // Initial fetch and re-fetch when parameters change
  useEffect(() => {
    fetchDirectoryStores();
  }, [fetchDirectoryStores]);

  return {
    stores,
    loading,
    error,
    pagination,
    openNowCount,
    refetch,
    fromCache,
    metrics: localMetrics,
  };
};
